import { useMemo } from 'react';
import { skipToken, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FieldValues, useController } from 'react-hook-form';

import { AutocompleteField } from 'common/forms/Field';
import {
    ICertificateInputOptionFragment,
    SkillCertificatesInputOptionDocument,
} from 'graphql/types';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';
import { TCertificateOption } from 'certificate/types';

const PAGE_SIZE = 50;

interface IProps {
    certificateOptions?: TCertificateOption[];
    disabled?: boolean;
    label?: string;
    name: string;
    noOptionsText?: string;
    skillId?: string;
}

export const SkillCertificateSelectField = <TFieldValues extends FieldValues>({
    certificateOptions,
    control: formControl,
    disabled,
    label,
    name,
    noOptionsText,
    skillId,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();

    const control = asTypedControl<TCertificateOption>(name, formControl);

    const {
        field,
        fieldState: { error },
    } = useController({ name, control });

    const { data, loading } = useQuery(
        SkillCertificatesInputOptionDocument,
        skillId && !certificateOptions
            ? { variables: { id: skillId }, fetchPolicy: 'no-cache' }
            : skipToken
    );

    const certificates = useMemo<TCertificateOption[]>(
        () =>
            certificateOptions ??
            (data?.skill?.skillCertificates || [])
                .map((sc) => sc?.certificate)
                .filter(
                    (cert): cert is ICertificateInputOptionFragment => !!cert
                ),
        [certificateOptions, data]
    );

    const value = field.value;
    const isDisabled = disabled || (!skillId && !certificateOptions);

    return (
        <AutocompleteField
            {...other}
            {...field}
            disabled={isDisabled}
            error={!!error}
            getOptionLabel={(option) => option.name}
            helperText={error?.message}
            InputProps={{
                label: label ?? translate('certificate'),
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loading}
            loadingText={translate('loadingDots')}
            noOptionsText={
                !loading
                    ? (noOptionsText ?? translate('noOptionsText.certificates'))
                    : translate('loadingDots')
            }
            options={certificates}
            slotProps={{
                listbox: {
                    component: ({
                        children,
                        ...other
                    }: React.ComponentProps<'ul'>) => (
                        <ul {...other}>
                            {children}
                            {certificates.length > PAGE_SIZE && (
                                <Box
                                    className="MuiAutocomplete-option"
                                    component="li"
                                    key="hasMore"
                                    sx={{
                                        pointerEvents: 'none',
                                        opacity: 0.6,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            width: '100%',
                                            textAlign: 'center',
                                        }}
                                        variant="caption"
                                    >
                                        {translate('moreResultsSelectSearch')}
                                    </Typography>
                                </Box>
                            )}
                        </ul>
                    ),
                },
            }}
            value={value || null}
            onChange={(_event, value) => {
                field.onChange(value);
            }}
        />
    );
};
